import { type FormEvent, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { authCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { TextField } from '@/ui/Field/Field'
import { Stack } from '@/ui/layout/Stack'
import { login } from '../api/auth'
import { AuthLink, AuthShell } from '../components/AuthShell'
import { ROUTES } from '../constants/routes'
import { useFeatures } from '../hooks/queries'
import { useAsyncSubmit } from '../hooks/useAsyncSubmit'
import { useAuthStore } from '../store/auth'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const setAuth = useAuthStore((s) => s.setAuth)
  const features = useFeatures()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const { loading, error, submit } = useAsyncSubmit()
  const successMessage = (location.state as { message?: string } | null)
    ?.message

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    await submit(async () => {
      const res = await login(username, password)
      setAuth(res.token, res.user_id, res.username, res.is_system_admin)
      void navigate(ROUTES.home, { replace: true })
    }, authCopy.login.failed)
  }

  return (
    <AuthShell>
      {/* One alert per region (Alert card): a sign-in error replaces the reset notice. */}
      {successMessage && !error && (
        <Alert tone="success">{successMessage}</Alert>
      )}
      <Stack as="form" gap="space-16" onSubmit={(e) => void handleSubmit(e)}>
        <TextField
          id="username"
          label={authCopy.username}
          value={username}
          onValue={setUsername}
          inputProps={{
            type: 'text',
            autoComplete: 'username',
            required: true,
          }}
        />
        <TextField
          id="password"
          label={authCopy.password}
          value={password}
          onValue={setPassword}
          inputProps={{
            type: 'password',
            autoComplete: 'current-password',
            required: true,
          }}
        />
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" variant="primary" loading={loading}>
          {authCopy.login.submit}
        </Button>
        {/* Reset delivers by email, so without SMTP the flow dead-ends. */}
        {features.email && (
          <AuthLink href={ROUTES.forgotPassword}>
            {authCopy.login.forgotPassword}
          </AuthLink>
        )}
      </Stack>
    </AuthShell>
  )
}
